import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api, mediaUrl } from '../api';
import PosTile from '../components/pos/PosTile';
import PosCart from '../components/pos/PosCart';
import PosModifierModal from '../components/pos/PosModifierModal';
import PosPaymentModal from '../components/pos/PosPaymentModal';
import PosCompletionSplash from '../components/pos/PosCompletionSplash';
import PosCafePrinterPill from '../components/pos/PosCafePrinterPill';
import PosRefundModal from '../components/pos/PosRefundModal';
import PosStationPicker from '../components/pos/PosStationPicker';
import PosPickupNameModal from '../components/pos/PosPickupNameModal';
import PosMenuDrawer from '../components/pos/PosMenuDrawer';
import PosDonationModal from '../components/pos/PosDonationModal';
import { getStationKey, getStationName, getDeviceId, clearStation } from '../lib/posStation';
import { applyKitchenEodDiscount } from '../lib/kitchenEodDiscount';
import sundayDrinksCutoff from '../lib/sundayDrinksCutoff';
import { TURKISH_COFFEE_KITCHEN } from '../lib/kitchenRoutes';
import { isNativePrintAvailable, printDocketsNative } from '../lib/nativePrint';
import {
  getPosStockSessionId,
  reservePosStock,
  releasePosStock,
  releaseAllPosStock,
  touchPosStock,
  stockHoldRefusedMessage,
} from '../lib/posStockHold';
import './PosPage.css';

// Poll interval for menu + stock. Matches the kitchen/cafe dashboards so
// stock changes made by app customers appear at the counter within one
// cycle (never more than 5 s of drift).
const STOCK_HOLD_REFRESH_MS = 2 * 60 * 1000;
const MENU_POLL_MS = 5000;

// Line key for cart deduplication. Items with different modifier selections
// remain separate cart lines even if they share the same menu_item_id.
function makeLineKey(itemId, options, variableAmount, comment) {
  const optString = options && Object.keys(options).length > 0
    ? JSON.stringify(options)
    : '';
  const amt = variableAmount != null ? `::$${Number(variableAmount).toFixed(2)}` : '';
  const note = comment ? `::n:${comment}` : '';
  return `${itemId}::${optString}${amt}${note}`;
}

function sanitizeLineComment(raw) {
  if (typeof raw !== 'string') return '';
  return raw.replace(/\s+/g, ' ').trim().slice(0, 80);
}

function buildOptionsLabel(options) {
  if (!options) return '';
  return Object.entries(options).filter(([, v]) => v).map(([, v]) => v).join(', ');
}

function cafeOrderIdsFromOrders(orders) {
  if (!Array.isArray(orders)) return [];
  return orders
    .filter((o) => o && o.venue === 'cafe' && Number(o.id) > 0)
    .map((o) => Number(o.id));
}

function friendlyKitchenName(venue, kitchen) {
  if (venue === 'cafe') return 'Cafe';
  if (kitchen === 'st_barbara') return 'St Barbara';
  if (kitchen === 'st_noufer') return 'St Noufer';
  if (kitchen === TURKISH_COFFEE_KITCHEN) return 'Turkish Coffee';
  if (venue === 'kitchen') return 'Kitchen';
  return kitchen || venue || 'Kitchen';
}

// POS menu bucket order: St Noufer → St Barbara → other kitchens → cafe
// food → drinks → counter. One continuous grid, no row breaks between
// groups. Within each bucket, categories/items keep their admin sort_order.
const POS_BUCKET_ORDER = ['st_noufer', 'st_barbara', TURKISH_COFFEE_KITCHEN, 'kitchen_other', 'cafe_food', 'drinks', 'counter'];
const POS_BUCKET_INDEX = Object.fromEntries(POS_BUCKET_ORDER.map((k, i) => [k, i]));

// Items for one POS tab. Both tabs are built up front so switching does
// not destroy tiles and reload their photos.
function itemsForPosTab(menu, tab) {
  const rows = [];
  for (const cat of menu) {
    const bucketKey = posSectionKey(cat);
    if (tab === 'desserts' ? bucketKey !== 'desserts' : bucketKey === 'desserts') continue;
    const bucketIdx = POS_BUCKET_INDEX[bucketKey] ?? 99;
    const catSort = cat.sort_order ?? 999;
    const drinkKind = sundayDrinksCutoff.sundayDrinkKind(cat);
    for (const it of cat.items || []) {
      rows.push({
        item: drinkKind ? { ...it, drinkKind } : it,
        bucketIdx,
        catSort,
        itemSort: it.sort_order ?? 999,
      });
    }
  }
  rows.sort((a, b) => (
    a.bucketIdx - b.bucketIdx
    || a.catSort - b.catSort
    || a.itemSort - b.itemSort
    || a.item.name.localeCompare(b.item.name)
  ));
  return rows.map((r) => r.item);
}

function posSectionKey(cat) {
  if (cat.venue === 'desserts') return 'desserts';
  if (Number(cat.collected_at_counter) === 1) return 'counter';
  if (cat.venue === 'cafe') return cat.is_drinks ? 'drinks' : 'cafe_food';
  if (cat.kitchen === 'st_noufer') return 'st_noufer';
  if (cat.kitchen === 'st_barbara') return 'st_barbara';
  if (cat.kitchen === TURKISH_COFFEE_KITCHEN) return TURKISH_COFFEE_KITCHEN;
  return 'kitchen_other';
}

// LocalStorage key for dismissing the "Add to Home Screen" hint banner.
// Bumped when we want everyone to see the hint again (e.g. after redesigning
// the install flow).
const PWA_HINT_DISMISSED_KEY = 'pos-pwa-hint-dismissed-v1';

// True when the page is running as an installed iOS PWA (added to Home
// Screen and launched from that icon) OR any browser reporting standalone
// via the display-mode media query. Both are needed because iOS Safari
// uses `navigator.standalone` and Android/desktop use the media query.
function isRunningStandalone() {
  if (typeof window === 'undefined') return false;
  if (window.navigator.standalone === true) return true;
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  return false;
}

// True when we're in iOS Safari (not in-app browsers like CriOS/FxiOS/EdgiOS
// that can't Add to Home Screen with the standalone display mode iOS applies
// only to native Safari).
function isIOSSafari() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (!isIOS) return false;
  return !/CriOS|FxiOS|EdgiOS/.test(ua);
}

export default function PosPage() {
  const [menu, setMenu] = useState([]); // array of categories, each with .items
  const [posTab, setPosTab] = useState('counter');
  const [config, setConfig] = useState(null);
  const [cart, setCart] = useState([]);
  const cartRef = useRef(cart);
  cartRef.current = cart;
  const [modal, setModal] = useState(null);
  const [nowClock, setNowClock] = useState(() => new Date());
  const [menuError, setMenuError] = useState('');
  // Which pos_stations row this iPad is bound to. Persists in localStorage
  // via lib/posStation.js. Null until the operator taps a tile in
  // PosStationPicker; the picker replaces the whole POS UI while null so
  // no sale can be rung against no station.
  const [stationKey, setStationKey] = useState(() => getStationKey());
  const [stationName, setStationName] = useState(() => getStationName());
  // Pickup name required when the cart contains cafe items — printed on
  // the cafe Zebra sticker so staff can call the customer. Cleared with
  // the cart after each sale. Kitchen-only carts never ask for a name.
  const [pickupName, setPickupName] = useState('');
  // Counter-only: mark cafe sale as Sunday School servant → reverse
  // SUNDAY SCHOOL banner on Zebra stickers (same as approved app accounts).
  const [sundaySchool, setSundaySchool] = useState(false);
  const [menuDrawerOpen, setMenuDrawerOpen] = useState(false);
  const [reprintNumber, setReprintNumber] = useState('');
  const [reprintBusy, setReprintBusy] = useState(false);
  // PWA install hint state: only show on iOS Safari that isn't already
  // running standalone AND hasn't been dismissed by the operator. Computed
  // once on mount so it doesn't flicker as the user navigates around.
  const [showPwaHint, setShowPwaHint] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      if (window.localStorage?.getItem(PWA_HINT_DISMISSED_KEY) === '1') return false;
    } catch {
      /* private-mode Safari throws on localStorage read — safe to ignore */
    }
    return isIOSSafari() && !isRunningStandalone();
  });
  const dismissPwaHint = useCallback(() => {
    try { window.localStorage?.setItem(PWA_HINT_DISMISSED_KEY, '1'); } catch { /* ignore */ }
    setShowPwaHint(false);
  }, []);

  const reloadConfig = useCallback(async () => {
    if (!stationKey) return null;
    try {
      const cfg = await api.getPosConfig(stationKey);
      setConfig(cfg);
      // Keep the header label in sync with whatever the server calls
      // this station right now (admin might have renamed it since the
      // iPad first picked).
      if (cfg?.station?.name) {
        setStationName((prev) => (prev !== cfg.station.name ? cfg.station.name : prev));
      }
      return cfg;
    } catch (err) {
      console.error('[pos] failed to load config:', err.message);
      return null;
    }
  }, [stationKey]);

  useEffect(() => {
    if (!stationKey) return undefined;
    let cancelled = false;
    const dropClaim = () => {
      clearStation();
      setStationKey('');
      setStationName('');
    };
    const verifyClaim = () => {
      api.listPosStations(getDeviceId())
        .then((rows) => {
          if (cancelled) return;
          const mine = (Array.isArray(rows) ? rows : []).find((r) => r.station_key === stationKey);
          if (!mine || !mine.claimed_by_me) dropClaim();
        })
        .catch(() => { /* stay on local station if the check fails */ });
    };
    verifyClaim();
    const poll = window.setInterval(verifyClaim, 2 * 60 * 1000);
    return () => {
      cancelled = true;
      window.clearInterval(poll);
    };
  }, [stationKey]);

  // Keep the claim alive while staff are actually tapping the POS. An
  // idle open screen does not heartbeat, so a forgotten iPad frees after
  // two hours.
  useEffect(() => {
    if (!stationKey) return undefined;
    let lastInteracted = Date.now();
    const mark = () => { lastInteracted = Date.now(); };
    const beat = () => {
      if (Date.now() - lastInteracted > 2 * 60 * 60 * 1000) return;
      api.heartbeatPosStation(stationKey, getDeviceId())
        .then((res) => {
          if (res && res.alive === false) {
            clearStation();
            setStationKey('');
            setStationName('');
          }
        })
        .catch(() => { /* next poll will catch a lost claim */ });
    };
    window.addEventListener('pointerdown', mark);
    const id = window.setInterval(beat, 5 * 60 * 1000);
    return () => {
      window.removeEventListener('pointerdown', mark);
      window.clearInterval(id);
    };
  }, [stationKey]);

  useEffect(() => {
    if (!stationKey) return undefined; // don't fetch config until a station is picked
    let cancelled = false;
    reloadConfig().then((cfg) => {
      if (cancelled || !cfg) return;
    });
    // Admin may flip eftpos_provider while this iPad stays open — refresh
    // when the app comes back to the foreground.
    const onFocus = () => { reloadConfig(); };
    const onVis = () => {
      if (document.visibilityState === 'visible') reloadConfig();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [stationKey, reloadConfig]);

  const reloadMenu = useCallback(async () => {
    try {
      const m = await api.getMenu();
      setMenu(m || []);
      setMenuError('');
    } catch (err) {
      setMenuError(err.message || 'Failed to load menu');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    async function tick() {
      await Promise.all([
        reloadMenu(),
        stationKey ? reloadConfig() : Promise.resolve(),
      ]);
      if (!cancelled) timer = setTimeout(tick, MENU_POLL_MS);
    }
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [reloadMenu, reloadConfig, stationKey]);

  const holdOrAlert = useCallback(async (item, quantity) => {
    const result = await reservePosStock(item?.id, quantity);
    if (!result.reserved) {
      window.alert(stockHoldRefusedMessage(item?.name || 'That item', result.available));
      void reloadMenu();
      return false;
    }
    // Refresh tiles so this station's last unit greys out immediately
    // (the hold counts against displayed stock, including our own).
    void reloadMenu();
    return true;
  }, [reloadMenu]);

  useEffect(() => {
    if (cart.length === 0) return undefined;
    touchPosStock();
    const id = window.setInterval(touchPosStock, STOCK_HOLD_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [cart.length]);

  useEffect(() => () => {
    releaseAllPosStock();
  }, []);

  useEffect(() => {
    const id = setInterval(() => setNowClock(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  // Swap the PWA manifest + iOS meta tags while /pos is mounted so that
  // "Add to Home Screen" produces a POS install (start_url=/pos, name
  // "St Barb POS") instead of the customer manifest, whose start_url is
  // "/" and would silently reopen the customer app every time.
  //
  // The primary swap actually happens in public/index.html via an inline
  // script that runs synchronously on first paint — that catches the case
  // where the operator installs from the PIN gate before this component
  // even mounts. This useEffect is the belt-and-braces path for
  // client-side SPA navigation (e.g. going from /admin to /pos without a
  // full reload), and restores the originals on unmount so anyone who
  // leaves /pos in the same tab doesn't end up installing the customer
  // app under the POS manifest.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const manifestLink = document.querySelector('link[rel="manifest"]');
    const appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    const themeColor = document.querySelector('meta[name="theme-color"]');

    const originals = {
      manifest: manifestLink?.getAttribute('href') || null,
      appleTitle: appleTitle?.getAttribute('content') || null,
      themeColor: themeColor?.getAttribute('content') || null,
      documentTitle: document.title,
    };

    if (manifestLink) manifestLink.setAttribute('href', '/pos-manifest.json');
    if (appleTitle) appleTitle.setAttribute('content', 'St Barb POS');
    if (themeColor) themeColor.setAttribute('content', '#f0e9df');
    document.title = 'St Barb POS';

    return () => {
      if (manifestLink && originals.manifest) manifestLink.setAttribute('href', originals.manifest);
      if (appleTitle && originals.appleTitle) appleTitle.setAttribute('content', originals.appleTitle);
      if (themeColor && originals.themeColor) themeColor.setAttribute('content', originals.themeColor);
      document.title = originals.documentTitle;
    };
  }, []);

  // Lock <html> and <body> scroll while /pos is mounted. Even though
  // .pos-page uses height: 100vh + overflow: hidden, iOS Safari still
  // lets the body itself rubber-band because the body is the ultimate
  // scroll root — which shows up as the top banner scrolling off screen
  // when a fast swipe reaches the top of the tile grid. Adding the
  // "pos-locked" class puts overflow: hidden + overscroll-behavior: none
  // on html/body so the only scrolling area is the internal grid wrap.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    document.documentElement.classList.add('pos-locked');
    document.body.classList.add('pos-locked');
    return () => {
      document.documentElement.classList.remove('pos-locked');
      document.body.classList.remove('pos-locked');
    };
  }, []);

  // Both tabs stay mounted. Hiding the other one (instead of removing
  // its tiles) keeps photos decoded, so switching does not flash grey.
  // Sort, within a tab:
  //   1. bucket order  — St Noufer → St Barbara → other kitchens → cafe
  //      food → drinks → counter. Desserts is its own tab.
  //   2. category.sort_order — respects admin drag-and-drop within a
  //      bucket (e.g. mains before sides inside St Barbara)
  //   3. item.sort_order — respects admin drag-and-drop within a category
  //   4. item.name — final tiebreaker so identical sort_orders are still
  //      deterministic across renders
  const counterItems = useMemo(() => itemsForPosTab(menu, 'counter'), [menu]);
  const dessertItems = useMemo(() => itemsForPosTab(menu, 'desserts'), [menu]);

  const eodOn = !!config?.kitchen_eod_discount;
  const sundayCloseBanner = sundayDrinksCutoff.sundayDrinkCloseBanner(
    nowClock,
    config?.sunday_drinks_cutoff
  );
  const drinkClosed = useCallback((item) => (
    sundayDrinksCutoff.isSundayDrinkClosed(item?.drinkKind, nowClock, config?.sunday_drinks_cutoff)
  ), [nowClock, config]);

  const addToCart = useCallback((item, quantity, options, surcharge, customAmount) => {
    const optionsClean = options && Object.keys(options).length > 0 ? options : null;
    const variableAmount = customAmount != null ? customAmount : null;
    const lineKey = makeLineKey(item.id, optionsClean, variableAmount);
    const listUnitPrice = (variableAmount != null ? variableAmount : item.price) + (surcharge || 0);
    const unitPrice = applyKitchenEodDiscount(listUnitPrice, item, eodOn);
    const optionsLabel = buildOptionsLabel(optionsClean);
    setCart((prev) => {
      const idx = prev.findIndex((l) => l.lineKey === lineKey);
      if (idx >= 0) {
        const next = prev.slice();
        next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
        return next;
      }
      return [
        ...prev,
        {
          lineKey, item, quantity, options: optionsClean, unitPrice, listUnitPrice,
          optionsLabel, variableAmount, comment: '',
        },
      ];
    });
  }, [eodOn]);

  const replaceLine = useCallback((oldKey, item, quantity, options, surcharge) => {
    const optionsClean = options && Object.keys(options).length > 0 ? options : null;
    const qty = quantity > 0 ? quantity : 1;
    setCart((prev) => {
      const existing = prev.find((l) => l.lineKey === oldKey);
      const comment = existing?.comment || '';
      const variableAmount = existing?.variableAmount ?? null;
      const newKey = makeLineKey(item.id, optionsClean, variableAmount, comment);
      const listUnitPrice = (variableAmount != null ? variableAmount : item.price) + (surcharge || 0);
      const unitPrice = applyKitchenEodDiscount(listUnitPrice, item, eodOn);
      const optionsLabel = buildOptionsLabel(optionsClean);
      const nextLine = {
        lineKey: newKey,
        item,
        quantity: qty,
        options: optionsClean,
        unitPrice,
        listUnitPrice,
        optionsLabel,
        variableAmount,
        comment,
      };
      if (newKey === oldKey) {
        return prev.map((l) => (l.lineKey === oldKey ? nextLine : l));
      }
      const oldIdx = prev.findIndex((l) => l.lineKey === oldKey);
      const without = prev.filter((l) => l.lineKey !== oldKey);
      const mergeIdx = without.findIndex((l) => l.lineKey === newKey);
      if (mergeIdx >= 0) {
        const next = without.slice();
        next[mergeIdx] = { ...next[mergeIdx], quantity: next[mergeIdx].quantity + qty };
        return next;
      }
      const insertAt = oldIdx >= 0 ? Math.min(oldIdx, without.length) : without.length;
      const next = without.slice();
      next.splice(insertAt, 0, nextLine);
      return next;
    });
  }, [eodOn]);

  useEffect(() => {
    setCart((prev) => prev.map((line) => {
      const list = line.listUnitPrice != null
        ? line.listUnitPrice
        : line.unitPrice;
      const nextPrice = applyKitchenEodDiscount(list, line.item, eodOn);
      if (nextPrice === line.unitPrice && line.listUnitPrice === list) return line;
      return { ...line, listUnitPrice: list, unitPrice: nextPrice };
    }));
  }, [eodOn]);

  const setLineComment = useCallback((lineKey, raw) => {
    const comment = sanitizeLineComment(raw);
    setCart((prev) => {
      const src = prev.find((l) => l.lineKey === lineKey);
      if (!src) return prev;
      const newKey = makeLineKey(src.item.id, src.options, src.variableAmount, comment);
      if (newKey === lineKey) {
        return prev.map((l) => (l.lineKey === lineKey ? { ...l, comment } : l));
      }
      const others = prev.filter((l) => l.lineKey !== lineKey);
      const mergeIdx = others.findIndex((l) => l.lineKey === newKey);
      if (mergeIdx >= 0) {
        const next = others.slice();
        next[mergeIdx] = { ...next[mergeIdx], quantity: next[mergeIdx].quantity + src.quantity };
        return next;
      }
      return prev.map((l) => (l.lineKey === lineKey ? { ...l, comment, lineKey: newKey } : l));
    });
  }, []);

  const updateLineQty = useCallback((lineKey, delta) => {
    setCart((prev) => {
      const next = prev.map((l) => (
        l.lineKey === lineKey ? { ...l, quantity: l.quantity + delta } : l
      ));
      return next.filter((l) => l.quantity > 0);
    });
  }, []);

  const removeLine = useCallback((lineKey) => {
    setCart((prev) => prev.filter((l) => l.lineKey !== lineKey));
  }, []);

  const incrementLine = useCallback(async (lineKey) => {
    const line = cartRef.current.find((l) => l.lineKey === lineKey);
    if (!line) return;
    const ok = await holdOrAlert(line.item, 1);
    if (!ok) return;
    updateLineQty(lineKey, 1);
  }, [holdOrAlert, updateLineQty]);

  const decrementLine = useCallback((lineKey) => {
    const line = cartRef.current.find((l) => l.lineKey === lineKey);
    if (!line) return;
    releasePosStock(line.item.id, 1);
    updateLineQty(lineKey, -1);
  }, [updateLineQty]);

  const removeHeldLine = useCallback((lineKey) => {
    const line = cartRef.current.find((l) => l.lineKey === lineKey);
    if (!line) return;
    releasePosStock(line.item.id, line.quantity);
    removeLine(lineKey);
  }, [removeLine]);

  const clearCart = useCallback(() => {
    if (cartRef.current.length > 0) releaseAllPosStock();
    setCart([]);
    setPickupName('');
    setSundaySchool(false);
  }, []);

  const cartHasCafe = useMemo(
    () => cart.some((l) => l.item?.venue === 'cafe'),
    [cart]
  );
  const cartHasTurkishCoffee = useMemo(
    () => cart.some((l) => l.item?.kitchen === TURKISH_COFFEE_KITCHEN),
    [cart]
  );
  const cartNeedsPickupName = cartHasCafe || cartHasTurkishCoffee;

  // Drop the Sunday School flag when the cart no longer has cafe items.
  useEffect(() => {
    if (!cartHasCafe && sundaySchool) setSundaySchool(false);
  }, [cartHasCafe, sundaySchool]);

  const total = useMemo(
    () => cart.reduce((sum, l) => sum + l.unitPrice * l.quantity, 0),
    [cart]
  );

  // "Fly to cart" chip animations. Each entry is a short-lived visual
  // spawned by a tap on a tile (or a modifier confirm) that arcs toward
  // the cart panel and fades out, so the operator has a clear "yes I
  // registered that tap" cue even on a busy stack of same-name items.
  // Purely cosmetic — never blocks the add-to-cart update itself and is
  // safe to yank out by clearing this state + removing the CSS.
  const [flyers, setFlyers] = useState([]);

  const spawnFlyer = useCallback((sourceRect, item) => {
    if (!sourceRect || !item) return;
    if (typeof document === 'undefined') return;
    // Respect the OS "reduce motion" preference — flyers are a purely
    // decorative motion cue.
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const target = document.querySelector('.pos-cart');
    if (!target) return;
    const targetRect = target.getBoundingClientRect();
    const from = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };
    const to = {
      x: targetRect.left + targetRect.width / 2,
      y: targetRect.top + 48,
    };
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const imageSrc = item.image_url ? mediaUrl(item.image_url) : null;
    setFlyers((prev) => [...prev, { id, from, to, label: item.name, imageSrc }]);
    // Match the CSS animation duration below (700ms) plus a small buffer
    // so React never unmounts a flyer mid-transition.
    window.setTimeout(() => {
      setFlyers((prev) => prev.filter((f) => f.id !== id));
    }, 800);
    // Very small bump on the cart panel just as the flyer lands. Kept
    // subtle to match the watermark style of the flyer itself.
    window.setTimeout(() => {
      const el = document.querySelector('.pos-cart');
      if (!el) return;
      el.classList.add('pos-cart-just-added');
      window.setTimeout(() => el.classList.remove('pos-cart-just-added'), 320);
    }, 540);
  }, []);

  const handleTileClick = useCallback(async (item, event) => {
    if (item.sold_out || drinkClosed(item)) return;
    const rect = event?.currentTarget?.getBoundingClientRect?.();
    const hasModifiers = item.modifier_groups && item.modifier_groups.length > 0;
    if (item.variable_price) {
      setModal({ kind: 'donation', item, sourceRect: rect });
      return;
    }
    if (!hasModifiers) {
      const ok = await holdOrAlert(item, 1);
      if (!ok) return;
      addToCart(item, 1, null, 0);
      spawnFlyer(rect, item);
      return;
    }
    // Stash the tile's rect on the modal so a successful modifier confirm
    // can fly the watermark from the original tile position (not the
    // modal), which reads as a much more natural "you added THAT item".
    setModal({ kind: 'modifier', item, sourceRect: rect });
  }, [addToCart, spawnFlyer, holdOrAlert, drinkClosed]);

  const handleModifierConfirm = useCallback(async ({ item, options, surcharge, quantity }) => {
    const qty = quantity > 0 ? quantity : 1;
    if (modal?.editLineKey) {
      const existing = cartRef.current.find((l) => l.lineKey === modal.editLineKey);
      const oldQty = existing?.quantity || 0;
      const sameItem = existing && Number(existing.item.id) === Number(item.id);
      if (sameItem && qty > oldQty) {
        const ok = await holdOrAlert(item, qty - oldQty);
        if (!ok) return;
      } else if (sameItem && qty < oldQty) {
        releasePosStock(item.id, oldQty - qty);
      } else if (!sameItem) {
        const ok = await holdOrAlert(item, qty);
        if (!ok) return;
        if (existing) releasePosStock(existing.item.id, oldQty);
      }
      replaceLine(modal.editLineKey, item, qty, options, surcharge);
    } else {
      if (drinkClosed(item)) {
        setModal(null);
        return;
      }
      const ok = await holdOrAlert(item, qty);
      if (!ok) return;
      addToCart(item, qty, options, surcharge);
      if (modal?.sourceRect) spawnFlyer(modal.sourceRect, item);
    }
    setModal(null);
  }, [addToCart, replaceLine, spawnFlyer, modal, holdOrAlert, drinkClosed]);

  const handleEditPickup = useCallback(() => {
    setModal({ kind: 'pickupName', purpose: 'edit' });
  }, []);

  const handleEditLine = useCallback((line) => {
    if (!line?.item) return;
    setModal({
      kind: 'modifier',
      item: line.item,
      editLineKey: line.lineKey,
      initialSelections: line.options || {},
      initialQuantity: line.quantity,
    });
  }, []);

  const buildOrderPayload = useCallback((paymentMethod, counterMeta) => ({
    // Cafe items need a real pickup name on the slip. Kitchen-only sales
    // keep the legacy "Counter" placeholder so reports stay consistent.
    customer_name: (cartNeedsPickupName && pickupName.trim())
      ? pickupName.trim().slice(0, 60)
      : 'Counter',
    items: cart.map((l) => ({
      menu_item_id: l.item.id,
      quantity: l.quantity,
      options: l.options || undefined,
      ...(l.comment ? { comment: l.comment } : {}),
      ...(l.variableAmount != null || l.item?.variable_price
        ? { unit_price: l.unitPrice }
        : {}),
    })),
    payment_method: paymentMethod,
    counter_meta: counterMeta,
    station_name: config?.counter_station_name || stationName || 'Station 1',
    // Server resolves station_key to pos_station_id and stamps it on the
    // order so the docket routes to this station's printer and future
    // Tyro Pay Instore calls use this station's TID.
    station_key: stationKey || undefined,
    pos_station_id: config?.station?.id || undefined,
    // When we're in the native pos-app the iPad prints directly over
    // the LAN via the TcpPrint plugin. Signal that intent to the server
    // so it skips its own (Azure-side, unreachable) TCP push and
    // returns the pre-built docket payloads for us to send instead.
    client_prints: isNativePrintAvailable() || undefined,
    // Cafe + Sunday School servant → sticker banner (server ignores for
    // kitchen-only / non-counter payment methods).
    sunday_school_servant: cartHasCafe && sundaySchool ? 1 : 0,
    // Same stock_reservations session as this iPad's cart hold. Server
    // deletes those rows after the sale deducts on-hand stock.
    session_id: getPosStockSessionId(),
  }), [cart, cartHasCafe, cartNeedsPickupName, pickupName, sundaySchool, config, stationKey, stationName]);

  const finaliseSale = useCallback(async (paymentMethod, counterMeta) => {
    if (cartNeedsPickupName && !pickupName.trim()) {
      window.alert('Pickup name is required for cafe and Turkish coffee orders.');
      throw new Error('Pickup name required');
    }
    const { payments, ...meta } = counterMeta || {};
    const payload = {
      ...buildOrderPayload(paymentMethod, meta),
      payments: Array.isArray(payments) ? payments : undefined,
    };
    try {
      const result = await api.createCounterOrder(payload);
      const orders = Array.isArray(result.orders) ? result.orders : [result];

      // Native-print path: server has returned base64 ESC/POS payloads
      // ready for the TcpPrint plugin to push to the LAN printer. We
      // run these in parallel with the completion splash so staff never
      // wait on the printer to see "Sale complete" — a slow printer
      // must not stall the next customer. Failures are collected and
      // surfaced quietly at the top of the splash so staff can reprint
      // just the failed kitchen without cancelling the sale.
      let printFailures = [];
      if (Array.isArray(result.dockets) && result.dockets.length > 0 && isNativePrintAvailable()) {
        // Fire and forget; capture failures via a chained then so React
        // can update the completion splash with a "1 docket failed"
        // toast without blocking closeModal on the socket.
        printDocketsNative(result.dockets)
          .then((printRes) => {
            if (printRes.failed.length > 0) {
              printFailures = printRes.failed;
              // Log so Datadog picks it up; the splash-level UI hint
              // is a future improvement (JIRA once observed in prod).
              console.warn('[pos] native print had failures:', printFailures);
            }
          })
          .catch((err) => {
            // Should never happen — printDocketsNative doesn't throw —
            // but guard anyway so a plugin bug can't wedge the sale.
            console.error('[pos] native print threw unexpectedly:', err);
          });
      }

      setModal({
        kind: 'completion',
        orders,
        total,
        paymentMethod,
        counterMeta: meta,
      });
      clearCart();
      return { ok: true, orders };
    } catch (err) {
      const message = err.message || 'Sale failed';
      window.alert(`Sale failed: ${message}`);
      throw err;
    }
  }, [buildOrderPayload, cartNeedsPickupName, pickupName, total, clearCart]);

  const openPaymentModal = useCallback(async () => {
    // Pull latest eftpos_provider before opening Take payment (admin may
    // have flipped it while this screen stayed open). Pass provider into
    // modal state so we don't race React's setConfig re-render.
    const cfg = await reloadConfig();
    setModal({
      kind: 'payment',
      total,
      provider: cfg?.eftpos_provider || 'manual',
    });
  }, [reloadConfig, total]);

  const handleTakePayment = useCallback(async () => {
    if (cart.length === 0) return;
    // Cafe items → collect pickup name first, then open payment.
    if (cartNeedsPickupName && !pickupName.trim()) {
      setModal({ kind: 'pickupName' });
      return;
    }
    await openPaymentModal();
  }, [cart.length, cartNeedsPickupName, pickupName, openPaymentModal]);
  const closeModal = useCallback(() => setModal(null), []);

  const cartLinesForSplash = useMemo(() => {
    if (!modal || modal.kind !== 'completion') return [];
    return modal.orders.map((o) => ({
      orderNumber: o.order_number,
      kitchenName: friendlyKitchenName(o.venue, o.kitchen),
      total: o.total,
    }));
  }, [modal]);

  const cafeOrderIdsForSplash = useMemo(() => {
    if (!modal || modal.kind !== 'completion') return [];
    return cafeOrderIdsFromOrders(modal.orders);
  }, [modal]);

  const timeLabel = nowClock.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });

  // Full-screen station picker replaces the POS UI on any iPad that
  // hasn't picked a station yet. Once picked, the choice is persisted
  // via lib/posStation.js and we re-render with the full POS.
  if (!stationKey) {
    return (
      <PosStationPicker
        onPicked={(row) => {
          setStationKey(row.station_key);
          setStationName(row.name);
        }}
      />
    );
  }

  return (
    <div className="pos-page">
      {/* Portrait-orientation overlay. Always rendered; CSS `@media (orientation: portrait)`
          swaps it in and hides the rest of the POS so the layout doesn't render
          squashed while the iPad is held upright. */}
      <div className="pos-rotate-overlay" aria-hidden="true">
        <div className="pos-rotate-icon">↻</div>
        <div className="pos-rotate-headline">Rotate to landscape</div>
        <div className="pos-rotate-subtext">The counter POS is designed for landscape orientation.</div>
      </div>

      {/* "Install to Home Screen" hint — shown only on iOS Safari that isn't
          already running as an installed PWA. One-tap dismiss remembers the
          choice in localStorage. */}
      {showPwaHint && (
        <div className="pos-pwa-hint">
          <div className="pos-pwa-hint-text">
            <strong>Tip:</strong> Tap the <span className="pos-pwa-share-icon" aria-hidden="true">⬆</span> Share button below, then <strong>Add to Home Screen</strong> for full-screen POS mode (no Safari toolbar).
          </div>
          <button className="pos-pwa-hint-dismiss" onClick={dismissPwaHint} aria-label="Dismiss install hint">
            Dismiss
          </button>
        </div>
      )}

      <header className="pos-header">
        <div className="pos-header-left">
          <button
            type="button"
            className="pos-burger-btn"
            aria-label="Open menu board"
            aria-expanded={menuDrawerOpen}
            onClick={() => setMenuDrawerOpen(true)}
          >
            <span className="pos-burger-icon" aria-hidden="true" />
          </button>
          <img src="/logo.png" alt="St Barb Kitchen" className="pos-logo" />
          <h1 className="pos-title">St. Barbara POS</h1>
        </div>
        <div className="pos-header-center">
          <PosCafePrinterPill />
        </div>
        <div className="pos-header-right">
          <form
            className="pos-reprint-form"
            onSubmit={async (e) => {
              e.preventDefault();
              const orderNumber = reprintNumber.trim().toUpperCase();
              if (!orderNumber || reprintBusy) return;
              setReprintBusy(true);
              try {
                const clientPrints = isNativePrintAvailable();
                const result = await api.printKitchenDocket({ orderNumber, clientPrints });
                const reprintLabel = (result.order_numbers && result.order_numbers.length)
                  ? result.order_numbers.join(', ')
                  : (result.order_number || orderNumber);
                if (clientPrints && result.dockets?.length) {
                  const printed = await printDocketsNative(result.dockets);
                  if (printed.failed?.length) {
                    alert(`Reprint failed: ${printed.failed.map((f) => f.error).join(', ')}`);
                  } else {
                    alert(`Printed docket${result.dockets.length > 1 ? 's' : ''} for ${reprintLabel}`);
                    setReprintNumber('');
                  }
                } else {
                  alert(result.message || `Print requested for ${reprintLabel}`);
                  setReprintNumber('');
                }
              } catch (err) {
                alert(err.message || 'Reprint failed');
              } finally {
                setReprintBusy(false);
              }
            }}
          >
            <input
              type="text"
              className="pos-reprint-input"
              placeholder="B88"
              value={reprintNumber}
              onChange={(e) => setReprintNumber(e.target.value)}
              aria-label="Order number to reprint"
              disabled={reprintBusy}
            />
            <button
              type="submit"
              className="pos-settings-link"
              disabled={reprintBusy || !reprintNumber.trim()}
              title="Reprint this order number only"
            >
              {reprintBusy ? 'Printing…' : 'Reprint'}
            </button>
          </form>
          <button
            type="button"
            className="pos-settings-link"
            title="Refund items from today's order"
            onClick={() => setModal({ kind: 'refund' })}
          >
            Refund
          </button>
          <Link to="/pos/settings" className="pos-settings-link" title="POS settings">Settings</Link>
          <span className="pos-clock">{timeLabel}</span>
        </div>
      </header>

      {eodOn && (
        <div className="pos-eod-banner">End of day — kitchen 50% off</div>
      )}

      {sundayCloseBanner && (
        <div className="pos-eod-banner pos-sunday-close-banner">{sundayCloseBanner}</div>
      )}

      {menuError && <div className="pos-error-banner">Menu error: {menuError}</div>}

      <div className="pos-board-tabs" role="tablist" aria-label="POS menu">
        <button
          type="button"
          role="tab"
          aria-selected={posTab === 'counter'}
          className={`pos-board-tab${posTab === 'counter' ? ' is-active' : ''}`}
          onClick={() => setPosTab('counter')}
        >
          Counter
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={posTab === 'desserts'}
          className={`pos-board-tab pos-board-tab-desserts${posTab === 'desserts' ? ' is-active' : ''}`}
          onClick={() => setPosTab('desserts')}
        >
          Desserts
        </button>
      </div>

      <div className="pos-body pos-body-two-col">
        <main className="pos-main">
          <div className="pos-grid-wrap">
            {[
              ['counter', counterItems, 'No items configured yet. Add items in the admin panel.'],
              ['desserts', dessertItems, 'No desserts yet. Add them under the Desserts category in admin.'],
            ].map(([tab, items, emptyText]) => (
              <div
                key={tab}
                className={`pos-grid-panel${posTab === tab ? ' is-active' : ' is-inactive'}`}
                aria-hidden={posTab === tab ? undefined : true}
              >
                {items.length === 0 ? (
                  <div className="pos-empty">{emptyText}</div>
                ) : (
                  <div className="pos-grid">
                    {items.map((item) => (
                      <PosTile
                        key={`${item.id}::${item.category_id}`}
                        item={item}
                        eodOn={eodOn}
                        closed={drinkClosed(item)}
                        onClick={(e) => handleTileClick(item, e)}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </main>

        <PosCart
          cart={cart}
          total={total}
          pickupName={pickupName}
          sundaySchool={sundaySchool}
          onSundaySchoolChange={setSundaySchool}
          onIncrement={incrementLine}
          onDecrement={decrementLine}
          onRemove={removeHeldLine}
          onEditLine={handleEditLine}
          onSetComment={setLineComment}
          onEditPickup={handleEditPickup}
          onTakePayment={handleTakePayment}
          onClearCart={clearCart}
        />
      </div>

      {modal && modal.kind === 'modifier' && (
        <PosModifierModal
          item={modal.item}
          eodOn={eodOn}
          initialSelections={modal.initialSelections}
          initialQuantity={modal.initialQuantity}
          confirmLabel={modal.editLineKey ? 'Update item' : 'Add to cart'}
          onConfirm={handleModifierConfirm}
          onCancel={closeModal}
        />
      )}

      {modal && modal.kind === 'donation' && (
        <PosDonationModal
          item={modal.item}
          onConfirm={async (amount) => {
            if (drinkClosed(modal.item)) {
              setModal(null);
              return;
            }
            const ok = await holdOrAlert(modal.item, 1);
            if (!ok) return;
            addToCart(modal.item, 1, null, 0, amount);
            spawnFlyer(modal.sourceRect, modal.item);
            setModal(null);
          }}
          onCancel={closeModal}
        />
      )}

      {modal && modal.kind === 'pickupName' && (
        <PosPickupNameModal
          initialName={pickupName}
          purpose={modal.purpose || 'payment'}
          onConfirm={async (name) => {
            setPickupName(name);
            if (modal.purpose === 'edit') {
              setModal(null);
              return;
            }
            await openPaymentModal();
          }}
          onCancel={closeModal}
        />
      )}

      {modal && modal.kind === 'payment' && (
        <PosPaymentModal
          total={modal.total}
          cart={cart}
          pickupName={pickupName}
          provider={modal.provider || config?.eftpos_provider || 'manual'}
          certAutoReceipts={Boolean(config?.tyro_cert_auto_receipts)}
          onCashConfirmed={(cashAmount, change) => finaliseSale('cash', {
            tender_type: 'cash',
            amount_paid: cashAmount,
            change_given: change,
            payments: [
              { method: 'cash', amount: cashAmount, change_given: change, tender_type: 'cash' },
            ],
          })}
          onEftposIntegrated={async ({ split, cashAmount, cardAmount } = {}) => {
            const chargeCents = Math.round(Number(cardAmount) * 100);
            const paymentMethod = split ? 'split' : 'card_paid';
            const counterMeta = split
              ? {
                  tender_type: 'split',
                  cash_amount: cashAmount,
                  card_amount: cardAmount,
                  payments: [
                    { method: 'cash', amount: cashAmount, change_given: 0, tender_type: 'cash' },
                    { method: 'card', amount: cardAmount, change_given: 0, tender_type: 'card_tyro_pay_instore' },
                  ],
                }
              : {
                  tender_type: 'card_tyro_pay_instore',
                  payments: [
                    { method: 'card', amount: cardAmount ?? modal.total, change_given: 0, tender_type: 'card_tyro_pay_instore' },
                  ],
                };
            const { payments, ...meta } = counterMeta;
            const payload = {
              ...buildOrderPayload(paymentMethod, meta),
              payments: Array.isArray(payments) ? payments : undefined,
              await_eftpos: true,
              charge_amount_cents: chargeCents,
            };
            const result = await api.createCounterOrder(payload);
            if (!result?.pay_request_id) {
              throw new Error(result?.error || 'Terminal payment did not start');
            }
            return result;
          }}
          onEftposApproved={async ({ split, cashAmount, cardAmount, integrated, orders, dockets, payRequestId, hasReceipts } = {}) => {
            // Integrated path: order already exists; webhook approved + kitchen printed.
            // Tyro card receipts are never printed here — only via completion "Print card receipt".
            if (integrated) {
              const list = Array.isArray(orders) && orders.length
                ? orders
                : (Array.isArray(orders) ? orders : []);
              if (Array.isArray(dockets) && dockets.length > 0 && isNativePrintAvailable()) {
                printDocketsNative(dockets).catch(() => {});
              }
              setModal({
                kind: 'completion',
                orders: list.length ? list : [{ order_number: '—' }],
                total,
                paymentMethod: split ? 'split' : 'card_paid',
                counterMeta: split
                  ? { tender_type: 'split', cash_amount: cashAmount, card_amount: cardAmount }
                  : { tender_type: 'card_tyro_pay_instore' },
                payRequestId: payRequestId || null,
                hasReceipts: Boolean(hasReceipts),
              });
              clearCart();
              return { ok: true, orders: list };
            }
            if (split) {
              return finaliseSale('split', {
                tender_type: 'split',
                cash_amount: cashAmount,
                card_amount: cardAmount,
                payments: [
                  { method: 'cash', amount: cashAmount, change_given: 0, tender_type: 'cash' },
                  { method: 'card', amount: cardAmount, change_given: 0, tender_type: 'card_manual' },
                ],
              });
            }
            return finaliseSale('card_paid', {
              tender_type: 'card_manual',
              payments: [
                { method: 'card', amount: cardAmount ?? modal.total, change_given: 0, tender_type: 'card_manual' },
              ],
            });
          }}
          onCancel={closeModal}
        />
      )}

      {modal && modal.kind === 'refund' && (
        <PosRefundModal
          onDone={closeModal}
          onCancel={closeModal}
          certAutoReceipts={Boolean(config?.tyro_cert_auto_receipts)}
          tyroReady={config?.eftpos_provider === 'tyro_pay_instore'}
        />
      )}

      {modal && modal.kind === 'completion' && (
        <PosCompletionSplash
          lines={cartLinesForSplash}
          total={modal.total}
          paymentMethod={modal.paymentMethod}
          counterMeta={modal.counterMeta}
          payRequestId={modal.payRequestId}
          hasReceipts={modal.hasReceipts}
          certAutoReceipts={Boolean(config?.tyro_cert_auto_receipts)}
          cafeOrderIds={cafeOrderIdsForSplash}
          onDone={closeModal}
        />
      )}

      <PosMenuDrawer
        open={menuDrawerOpen}
        onClose={() => setMenuDrawerOpen(false)}
        onMenuChanged={reloadMenu}
      />

      {/* Cosmetic "fly to cart" watermarks. Position is set inline (fixed
          co-ordinates in viewport space) and the arc + fade is a pure CSS
          keyframe. Pointer-events: none in CSS keeps them from ever
          intercepting a rapid follow-up tap. When the item has an image
          we render a ghostly copy of it; if not, fall back to a barely-
          visible name label so tap acknowledgement is still there. */}
      {flyers.map((f) => (
        <div
          key={f.id}
          className={`pos-flyer ${f.imageSrc ? 'pos-flyer-img' : 'pos-flyer-text'}`}
          style={{
            left: `${f.from.x}px`,
            top: `${f.from.y}px`,
            '--flyer-dx': `${f.to.x - f.from.x}px`,
            '--flyer-dy': `${f.to.y - f.from.y}px`,
          }}
          aria-hidden="true"
        >
          {f.imageSrc ? (
            <img src={f.imageSrc} alt="" className="pos-flyer-image" draggable={false} />
          ) : (
            <span className="pos-flyer-label">{f.label}</span>
          )}
        </div>
      ))}
    </div>
  );
}
